// Navi API-Client
// ===============
//
// Wave 14bv Stage 5: dünner Wrapper um das POST /api/navi/finger-route
// Backend-Endpoint. Request-/Response-Types exakt wie in backend/routes/navi.js
// dokumentiert.
//
// Bewusst KEIN Offline-Queueing: finger-route läuft immer online (das Backend
// hat den Graph, der Client nicht). Wenn offline, muss die UI das dem User
// sagen und den Compute-Button disabled halten.

import type { Difficulty, RegionId } from '@adtu/shared';

export interface FingerRouteRequest {
  /** N Strokes, jeder Stroke = Array von [lng, lat]-Paaren. */
  strokes: Array<Array<[number, number]>>;
  /** Sample-Abstand entlang jedes Strokes in Metern (Default Backend: 200). */
  sampleStepM?: number;
  /** Snap-Radius Finger-Punkt → Graph-Knoten in Metern (Default Backend: 150). */
  snapRadiusM?: number;
  /**
   * Wave 14cf: Region-ID — selektiert die Routing-DB im Backend
   * (navi-{region}.sqlite). Default-Backend nutzt 'liguria' wenn leer.
   */
  region?: RegionId;
}

export interface FingerRouteResponse {
  /** Geroutete Polyline als [lng, lat]-Paare. */
  coordinates: Array<[number, number]>;
  /** Gesamtlänge der gerouteten Polyline in Metern. */
  lengthM: number;
  /**
   * Slugs der genutzten eigenen GPX-Trails (z.B. "1776716920491-catalano").
   * Kommt aus edges.source='gpx'-Kanten, leer wenn kein eigener Trail genutzt
   * wurde.
   */
  trailsUsed: string[];
  /**
   * Warnungen pro Segment: z.B. `no_path_3:12345->67890` wenn Dijkstra zwischen
   * zwei Waypoints keinen Pfad gefunden hat. Für MVP zeigt die UI einen
   * einfachen Hinweis "some parts couldn't be connected".
   */
  warnings: string[];
  /** Debug-Zähler aus dem Backend-Pipeline. */
  debug: {
    waypoints: number;
    snapped: number;
    deduped: number;
    segments: number;
  };
  /** Compute-Zeit in ms auf dem Backend. */
  computeMs: number;
  /** Graph-Load-Stats (einmalig beim Server-Start, nicht pro Request). */
  graphStats: {
    nodes: number;
    edges: number;
    loadMs: number;
  };
}

export class NaviRouteError extends Error {
  status: number;
  reason?: string;
  /**
   * Zusatzinfos aus dem Fehler-Body. Der Router braucht sie, um dem User
   * einen brauchbaren Satz zu zeigen statt nur „ausserhalb des Korridors":
   * bei `cross_segment` z. B. „Start und Ziel liegen in verschiedenen
   * Bereichen (von 3)", bei `navi_expired` den Namen der Region.
   */
  info?: { region?: string; corridorRegion?: string; areas?: number };
  constructor(
    status: number,
    message: string,
    reason?: string,
    info?: { region?: string; corridorRegion?: string; areas?: number },
  ) {
    super(message);
    this.name = 'NaviRouteError';
    this.status = status;
    this.reason = reason;
    this.info = info;
  }
}

// Wave 14ca-fix25: Timeout grosszügig auf 30s erhöht. Der erste Pin-Route-
// Request nach Backend-Restart muss den Graph kalt laden — bei der neuen
// Liguria-BBOX (1 Mio Nodes) dauert das 6–8s, plus ~400ms Routing, plus
// Netz-Jitter. Bei 15s wurde der erste Request gelegentlich abortet;
// User sah "Network error" obwohl der zweite Versuch in 100ms durchging.
const NAVI_TIMEOUT_MS = 30000;

// Wave 14ca-fix25: bei Netz-/Timeout-Fehler einmal stumm wiederholen.
// Beim ersten Versuch lädt der Backend-Graph kalt und kann das Timeout
// erreichen oder der Browser wirft NetworkError. Beim Retry hat das
// Backend den Graph in Memory → zweiter Request schliesst in <1s ab.
const NAVI_RETRY_DELAY_MS = 300;

/**
 * Wrapper um fetch() der einmal automatisch retryt bei Netz-/Timeout-/
 * 5xx-Fehler. HTTP-4xx (Validation, 404 etc) gehen direkt durch — die
 * würden auch beim Retry nicht plötzlich grün.
 */
async function fetchWithNaviRetry(
  url: string,
  init: RequestInit,
  timeoutMs: number = NAVI_TIMEOUT_MS,
): Promise<Response> {
  const attempt = async (signal: AbortSignal): Promise<Response> => {
    return fetch(url, { ...init, signal });
  };

  // Erster Versuch
  const ctrl1 = new AbortController();
  const timer1 = setTimeout(() => ctrl1.abort(), timeoutMs);
  let firstError: unknown = null;
  try {
    const res = await attempt(ctrl1.signal);
    // 5xx → retry; alles andere durchreichen
    if (res.status >= 500 && res.status < 600) {
      firstError = new Error(`status ${res.status}`);
    } else {
      return res;
    }
  } catch (err) {
    firstError = err;
  } finally {
    clearTimeout(timer1);
  }

  // Retry — kurze Pause damit Backend ggf. den Graph fertig lädt
  await new Promise((r) => setTimeout(r, NAVI_RETRY_DELAY_MS));
  const ctrl2 = new AbortController();
  const timer2 = setTimeout(() => ctrl2.abort(), timeoutMs);
  try {
    return await attempt(ctrl2.signal);
  } catch (err) {
    // Beim Retry-Fail werfen wir den ZWEITEN Fehler — der ist relevanter
    // (erster war ein bekannter Cold-Start-Spike).
    throw err;
  } finally {
    clearTimeout(timer2);
  }
}

/**
 * POST /api/navi/finger-route. Wirft bei Netz- oder HTTP-Fehler.
 * Das timeout-fence (15s) ist grosszügig: wenn der Graph kalt ist, dauert
 * der allererste Request 1–2s für den Load; ein Routing-Call selbst ist
 * normal <200ms auch bei langen Strokes.
 */
export async function postFingerRoute(
  body: FingerRouteRequest,
): Promise<FingerRouteResponse> {
  const res = await fetchWithNaviRetry('/api/navi/finger-route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const err = (await res.json()) as { error?: string; reason?: string };
      reason = err.reason || err.error;
    } catch {
      /* Body war kein JSON */
    }
    throw new NaviRouteError(
      res.status,
      `finger-route ${res.status}`,
      reason,
    );
  }
  return (await res.json()) as FingerRouteResponse;
}

// --------------------------------------------------------------------------
// Stage 6c — Pin-Pin-Guide-Routing
// --------------------------------------------------------------------------

/**
 * Stage 6m: einzelner Stop in einer geordneten Route. Trail = "fahr Trail
 * X komplett ab"; Via = "geh über diesen Punkt" (gesnappt auf nächsten
 * OSM-Knoten, kein Walk).
 */
export type PinRouteWaypoint =
  | { kind: 'trail'; trailId: string }
  | { kind: 'via'; point: [number, number] };

export interface PinRouteRequest {
  /** Exakter Startpunkt [lng, lat] — vom User per Tap gesetzt. */
  start: [number, number];
  /** Exakter Zielpunkt [lng, lat] — vom User per Tap gesetzt. */
  end: [number, number];
  /**
   * Wave 14ca-fix21 — Routing-Modus.
   *   'mtb'  (Default) → Trails freilich nutzbar, Connector skipt nur
   *                      Ride-Trails die der User nicht gewählt hat.
   *   'road'           → nur Asphaltstrassen + Service-Roads, alle Trails
   *                      und unbefestigte OSM-Wege werden geskippt. Für
   *                      "schnell+sicher heim" wenn Akku leer / dunkel.
   */
  mode?: 'mtb' | 'road' | 'flattest';
  /**
   * Stage 6m — geordnete Liste aus Trail- und Via-Stops zwischen Start
   * und End. Wenn gesetzt, ignoriert der Router das Legacy-`trailIds`-Feld.
   * Im Road-Mode werden Trail-Waypoints ignoriert (Vias bleiben aktiv).
   */
  waypoints?: PinRouteWaypoint[];
  /**
   * Stage 6e Legacy — geordnete Trail-IDs ohne Via-Pins. Wird ignoriert
   * sobald `waypoints` gesetzt ist. Bleibt im Type für Cross-Version-
   * Compat falls ein älterer Client die Liste noch so schickt.
   */
  trailIds?: string[];
  /**
   * Legacy (Stage 6c/6d) — grobe Richtungsstriche als Alternative zur
   * expliziten Trail-Auswahl. Wird vom Backend ignoriert wenn `trailIds`
   * gesetzt ist. Bleibt im Type damit ältere Clients nicht brechen.
   */
  guideStrokes?: Array<Array<[number, number]>>;
  /** Optional: override aus Admin-Config. Server-Defaults sind meistens OK. */
  sampleStepM?: number;
  pinSnapRadiusM?: number;
  trailMagnetRadiusM?: number;
  preferredTrailMultiplier?: number;
  preferredTrailMinSamples?: number;
  /**
   * Wave 14cf: Region-ID — wählt die Routing-DB im Backend.
   */
  region?: RegionId;
  /**
   * Wave 14cv: Max-Difficulty-Cap. Trails strict schwerer als dieser Wert
   * werden vom Router rausgefiltert (Connector-Skip + explizit gewählte
   * Trails kriegen Warning + werden geskippt). `null`/undefined = kein Filter.
   * 'unknown' Trails sind nie betroffen (konservativ-permissiv).
   */
  maxDifficulty?: Difficulty | null;
}

/**
 * Stage 6m-fix4: pro Route-Segment Index-Range in `coordinates`. Erlaubt
 * dem Frontend, die Polyline mehrfarbig zu rendern (Trail in Difficulty-
 * Farbe, Connector in Sky, Bridge rot-gestrichelt).
 */
export interface PinRouteSegment {
  kind: 'connector' | 'trail' | 'bridge';
  trailId: string | null;
  /** Index in `coordinates` (inklusive). */
  from: number;
  /** Index in `coordinates` (inklusive). */
  to: number;
}

export interface PinRouteResponse {
  coordinates: Array<[number, number]>;
  lengthM: number;
  /** ULTRA: Anstieg/Abstieg in Metern entlang der Route (aus Knoten-Höhen).
   *  Optional — ältere Backend-Builds liefern's nicht. */
  ascentM?: number;
  descentM?: number;
  trailsUsed: string[];
  warnings: string[];
  /** Stage 6m-fix4: optional, ältere Backend-Builds liefern's nicht. */
  routeSegments?: PinRouteSegment[];
  /**
   * Stage 6d-shape. Ältere Felder (`preferredTrails`, `trailHits`) werden
   * vom neuen Backend nicht mehr geliefert — die Locking-Logik produziert
   * stattdessen `trailsLocked` (geordnete Liste der aktiv befahrenen
   * Trails) und `segmentsDetected`/`segmentsWalked`.
   */
  debug: {
    guideSamples: number;
    segmentsDetected?: number;
    segmentsWalked?: number;
    trailsLocked?: string[];
    /** Stage 6m: Anzahl der via-Pins die geroutet wurden. */
    viasRouted?: number;
    startSnapM?: number;
    endSnapM?: number;
    // Legacy-Felder für Cross-Version-Compat; werden ignoriert wenn leer.
    preferredTrails?: string[];
    trailHits?: Record<string, number>;
    segments?: number;
    detect?: {
      totalSamples: number;
      labeled: number;
      runsRejected: number;
      perTrailCount: Record<string, number>;
    };
  };
  computeMs: number;
  graphStats: {
    nodes: number;
    edges: number;
    loadMs: number;
  };
}

/**
 * POST /api/navi/pin-route. Siehe Doku in backend/routes/navi.js.
 */
export async function postPinRoute(
  body: PinRouteRequest,
): Promise<PinRouteResponse> {
  const res = await fetchWithNaviRetry('/api/navi/pin-route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const err = (await res.json()) as { error?: string; reason?: string };
      reason = err.reason || err.error;
    } catch {
      /* Body war kein JSON */
    }
    throw new NaviRouteError(res.status, `pin-route ${res.status}`, reason);
  }
  return (await res.json()) as PinRouteResponse;
}

export interface DetourResponse {
  ok: boolean;
  reason?: string;
  /** Netto-Mehraufwand vs. Originalstrecke. */
  extraKm?: number;
  extraHm?: number;
  /** [lon,lat] optimaler Ausstieg / Wiedereinstieg auf der Rennstrecke. */
  exit?: [number, number];
  rejoin?: [number, number];
  /** Detour-Geometrie A→POI→B ([lon,lat][]). */
  coordinates?: Array<[number, number]>;
}

/**
 * POST /api/navi/detour — mathematisch optimaler Pit-Stop-Umweg.
 * @param poi [lon,lat]
 * @param route Vorwärts-Fenster der Rennstrecke als [lon,lat,ele?][]
 */
export async function postDetour(
  poi: [number, number],
  route: Array<[number, number, number?]>,
  region?: RegionId,
): Promise<DetourResponse> {
  const res = await fetchWithNaviRetry('/api/navi/detour', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ poi, route, region }),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const err = (await res.json()) as { error?: string; reason?: string };
      reason = err.reason || err.error;
    } catch {
      /* kein JSON */
    }
    throw new NaviRouteError(res.status, `detour ${res.status}`, reason);
  }
  return (await res.json()) as DetourResponse;
}

// Wave 14eq: Pin-Snap-Probe für visuelles Feedback. Wird nach jedem Pin-
// Tap in CreateCustomPage gefeuert, damit der User schon vor Calculate
// sieht, ob der Pin auf einer Strasse landet (gruener Border + Linie auf
// der Karte hervorgehoben) oder im Niemandsland (roter Border).
export interface SnapProbeResult {
  /** null wenn kein Way im Radius gefunden. */
  snap: {
    edgeId: number;
    wayId: string | null;
    highway: string | null;
    source: string | null;
    distM: number;
    fromNodeId: number;
    toNodeId: number;
    fromLat: number;
    fromLon: number;
    toLat: number;
    toLon: number;
    wayLengthM?: number | null;
    wayName?: string | null;
    wayGeometry?: {
      center: [number, number];
      bbox: [number, number, number, number];
      segments: Array<[[number, number], [number, number]]>;
    } | null;
  } | null;
}

export async function postSnapProbe(body: {
  lat: number;
  lon: number;
  region?: string;
  radiusM?: number;
}): Promise<SnapProbeResult> {
  const res = await fetchWithNaviRetry('/api/navi/snap-position', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Snap ist nice-to-have, kein hard fail. Bei Server-Fehler tun wir so
    // als waere kein Snap gefunden — Pin wird neutral angezeigt.
    return { snap: null };
  }
  return (await res.json()) as SnapProbeResult;
}

// --------------------------------------------------------------------------
// ULTRA — Road-Bike A→B-Router ("Route berechnen", 3. Navi-Kachel)
// --------------------------------------------------------------------------

/** Eine der drei Routen-Varianten. `null` wenn für dieses Profil kein Pfad. */
export interface RouteVariant {
  geometry: { type: 'LineString'; coordinates: Array<[number, number]> };
  /** Streckenlänge in Metern. */
  distanceM: number;
  /** Geschätzte Fahrzeit in Sekunden (Rennrad-Geschwindigkeiten pro Strasse). */
  durationS: number;
  /** Höhenmeter Anstieg (Totband-gefiltert). */
  ascentM: number;
  descentM: number;
}

export interface RouteABResponse {
  /** Absolut kürzeste Strecke. */
  shortest: RouteVariant | null;
  /** Schnellste Fahrzeit fürs Rennrad. */
  fastest: RouteVariant | null;
  /** Wenigste Höhenmeter. */
  easiest: RouteVariant | null;
  /** Gewählter Bereich (z. B. 3pbr-1 oder <korridor-slug>-2). */
  region: string;
  /** Korridor, zu dem der Bereich gehört (3pbr oder ein Self-Service-Slug). */
  corridorRegion?: string;
  /** Anzahl Bereiche dieses Korridors — für „Bereich x von y" in der UI. */
  corridorAreas?: number;
  computeMs: number;
  /** Snap-Distanzen Start/Ziel in Metern (Pin → nächste asphaltierte Strasse). */
  snap: { startM: number; endM: number };
}

/**
 * POST /api/navi/route — berechnet in EINEM Call alle drei Varianten
 * (kürzeste / schnellste / wenigste Höhenmeter) zwischen zwei Punkten.
 * Nur innerhalb des Korridors; wirft NaviRouteError (mit `reason`) bei
 * out_of_corridor / too_far_apart / no_road / no_route.
 */
// Der allererste Route-Request in einem noch nicht gewärmten Kartenbereich
// kann den Graph-Kaltbuild auslösen (~15s, am Server bis ~60s unter Last).
// Seit dem Boot-Prewarm passiert das praktisch nicht mehr — der grosszügige
// 5-Minuten-Timeout ist die Sicherheitsleine, damit der User nie fälschlich
// einen "Netzwerkfehler" sieht während der Server noch rechnet.
const NAVI_ROUTE_TIMEOUT_MS = 300000;

/**
 * POST /api/navi/resolve-url — löst einen gekürzten Google-Maps-Link
 * (maps.app.goo.gl / goo.gl/maps) server-seitig zu { lat, lon } auf.
 * Wirft NaviRouteError (mit reason) bei Fehler.
 */
export async function postResolveUrl(
  url: string,
): Promise<{ lat: number; lon: number }> {
  const res = await fetchWithNaviRetry('/api/navi/resolve-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    let reason: string | undefined;
    try {
      const err = (await res.json()) as { error?: string; reason?: string };
      reason = err.reason || err.error;
    } catch {
      /* kein JSON */
    }
    throw new NaviRouteError(res.status, `resolve-url ${res.status}`, reason);
  }
  return (await res.json()) as { lat: number; lon: number };
}

export async function postRouteAB(
  start: [number, number],
  end: [number, number],
  region?: string | null,
): Promise<RouteABResponse> {
  // `region` ist die aktive Karten-Region des Users (3pbr oder der Slug
  // seines selbst gebauten Korridors). Das Backend nimmt sie als bevorzugte
  // Quelle und sucht nur dann in anderen Korridoren, wenn dort nichts passt
  // — ohne diesen Wert würde in einem frisch gebauten Korridor immer der
  // 3PBR-Korridor gewinnen und „Punkt liegt ausserhalb der Karte" melden.
  const res = await fetchWithNaviRetry(
    '/api/navi/route',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(region ? { start, end, region } : { start, end }),
    },
    NAVI_ROUTE_TIMEOUT_MS,
  );
  if (!res.ok) {
    let reason: string | undefined;
    let info: { region?: string; corridorRegion?: string; areas?: number } | undefined;
    try {
      const err = (await res.json()) as {
        error?: string;
        reason?: string;
        region?: string;
        corridorRegion?: string;
        areas?: number;
      };
      reason = err.reason || err.error;
      info = { region: err.region, corridorRegion: err.corridorRegion, areas: err.areas };
    } catch {
      /* kein JSON */
    }
    throw new NaviRouteError(res.status, `route ${res.status}`, reason, info);
  }
  return (await res.json()) as RouteABResponse;
}
